/**
 * Alpha Trade Strategy — faithful port of Alpha_Trade_Source.afl
 *
 * Signal Generation:
 *   1. Trend Magic: CCI(50) crosses above/below zero → LONG/SHORT
 *   2. Bollinger Bands Stop: BB(10,5) price breaks → LONG/SHORT (with trailing)
 *   3. Combined: Both must agree for NEW entry; neutral when they disagree
 *
 * TP/SL (Fibonacci risk-based from AFL):
 *   - risk = |Entry − BB_STOP|
 *   - Target 1 = Entry ± 0.618 × risk
 *   - Target 2 = Entry ± 1.618 × risk
 *   - Stop Loss = BB_STOP at signal bar
 *
 * Candle Colors (AFL-faithful):
 *   - LONG signal: Green (#00B450)
 *   - SHORT signal: Red (#DC3232)
 *   - Neutral (BB ≠ TM): Gray (#787878)
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { inferTickSize, roundToTick } from "./price-utils";

const DIR_NONE = 0;
const DIR_LONG = 1;
const DIR_SHORT = -1;
type Direction = typeof DIR_NONE | typeof DIR_LONG | typeof DIR_SHORT;

const TP1_MULT = 0.618;
const TP2_MULT = 1.618;

// ─── CCI computation ─────────────────────────────────────────────
function computeCCI(candles: CandleData[], period: number): number[] {
  const len = candles.length;
  const tp: number[] = candles.map(c => (c.high + c.low + c.close) / 3);
  const cci: number[] = new Array(len).fill(0);
  for (let i = period - 1; i < len; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += tp[j];
    const sma = sum / period;
    let meanDev = 0;
    for (let j = i - period + 1; j <= i; j++) meanDev += Math.abs(tp[j] - sma);
    meanDev /= period;
    cci[i] = meanDev !== 0 ? (tp[i] - sma) / (0.015 * meanDev) : 0;
  }
  return cci;
}

// ─── Bollinger Bands ─────────────────────────────────────────────
function computeBBands(
  candles: CandleData[], period: number, deviation: number,
): { upper: number[]; lower: number[] } {
  const len = candles.length;
  const upper: number[] = new Array(len).fill(0);
  const lower: number[] = new Array(len).fill(0);
  for (let i = period - 1; i < len; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += candles[j].close;
    const sma = sum / period;
    let variance = 0;
    for (let j = i - period + 1; j <= i; j++) variance += (candles[j].close - sma) ** 2;
    const stdDev = Math.sqrt(variance / period);
    upper[i] = sma + deviation * stdDev;
    lower[i] = sma - deviation * stdDev;
  }
  return { upper, lower };
}

// ─── BB Stop with trailing (AFL exact match) ─────────────────────
function computeBBStop(
  candles: CandleData[], bbUpper: number[], bbLower: number[], moneyRisk: number,
): { signal: Direction[]; stop: number[] } {
  const len = candles.length;
  const signal: Direction[] = new Array(len).fill(DIR_NONE);
  const stop: number[] = new Array(len).fill(0);
  if (len < 2) return { signal, stop };
  let bbState: Direction = DIR_NONE;
  let prevUpper = bbUpper[0];
  let prevLower = bbLower[0];
  let prevSlLong = bbLower[0];
  let prevSlShort = bbUpper[0];
  for (let i = 1; i < len; i++) {
    if (candles[i].close > prevUpper) bbState = DIR_LONG;
    if (candles[i].close < prevLower) bbState = DIR_SHORT;
    let curUpper = bbUpper[i];
    let curLower = bbLower[i];
    if (bbState === DIR_LONG && curLower < prevLower) curLower = prevLower;
    if (bbState === DIR_SHORT && curUpper > prevUpper) curUpper = prevUpper;
    const slShort = curUpper + ((moneyRisk - 1.0) / 2.0) * (curUpper - curLower);
    const slLong = curLower - ((moneyRisk - 1.0) / 2.0) * (curUpper - curLower);
    let finalSlLong = slLong;
    let finalSlShort = slShort;
    if (bbState === DIR_LONG && slLong < prevSlLong) finalSlLong = prevSlLong;
    if (bbState === DIR_SHORT && slShort > prevSlShort) finalSlShort = prevSlShort;
    signal[i] = bbState;
    if (bbState === DIR_LONG) stop[i] = finalSlLong;
    else if (bbState === DIR_SHORT) stop[i] = finalSlShort;
    else stop[i] = stop[i - 1];
    prevUpper = curUpper;
    prevLower = curLower;
    prevSlLong = finalSlLong;
    prevSlShort = finalSlShort;
  }
  return { signal, stop };
}

// ─── Dashboard data interface ────────────────────────────────────
export interface AlphaTradeV2Dashboard {
  signal: "BUY" | "SELL" | null;
  trend: "UP" | "DOWN" | "NEUTRAL";
  entry: number | null;
  stopLoss: number | null;
  target1: number | null;
  target2: number | null;
  currentPrice: number;
  livePL: number;
  bookedPL: number;
  status: "Active" | "Target1 Hit" | "Target2 Hit" | "Stop Loss Hit";
  tp1Hit: boolean;
  tp2Hit: boolean;
  slHit: boolean;
  candleState: "long" | "short" | "neutral";
}

export const alphaTradeV2Indicator: IndicatorDefinition = {
  id: "alpha_trade_v2",
  name: "Alpha Trade",
  shortName: "ATV",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [
      { label: "Yes", value: "yes" }, { label: "No", value: "no" },
    ]},
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#FFFFFF", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#FFFF00", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target1", label: "Target 1", color: "#00FF00", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target2", label: "Target 2", color: "#00FF00", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const displayLevels = params.displayLevels ?? "yes";
    const len = candles.length;
    const emptyResult: PlotData[] = [
      { key: "entry", data: [] }, { key: "stopLoss", data: [] },
      { key: "target1", data: [] }, { key: "target2", data: [] },
    ];

    if (len < 55) return emptyResult;

    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));
    const showBackground = params.showBackground ?? "yes";
    // ═══════════════════════════════════════════════════════════════
    // 1. Core indicators
    // ═══════════════════════════════════════════════════════════════
    const cci = computeCCI(candles, 50);
    const { upper: bbUpper, lower: bbLower } = computeBBands(candles, 10, 5);
    const { signal: bbSignal, stop: bbStop } = computeBBStop(candles, bbUpper, bbLower, 1.0);

    // ═══════════════════════════════════════════════════════════════
    // 2. Trend Magic — CCI crosses zero (forward-filled)
    // ═══════════════════════════════════════════════════════════════
    const tmSignal: Direction[] = new Array(len).fill(DIR_NONE);
    for (let i = 1; i < len; i++) {
      if (cci[i] > 0 && cci[i - 1] <= 0) tmSignal[i] = DIR_LONG;
      else if (cci[i] < 0 && cci[i - 1] >= 0) tmSignal[i] = DIR_SHORT;
      else tmSignal[i] = tmSignal[i - 1];
    }

    // ═══════════════════════════════════════════════════════════════
    // 3. Combined signal — both must agree (AFL faithful)
    // ═══════════════════════════════════════════════════════════════
    const markers: IndicatorMarker[] = [];
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number; type: "signal" | "tp1" | "tp2" | "sl"; position: "above" | "below" }[] = [];
    const SIGNAL: Direction[] = new Array(len).fill(DIR_NONE);
    const ENTRY: number[] = new Array(len).fill(0);
    const SL: number[] = new Array(len).fill(0);
    const TP1: number[] = new Array(len).fill(0);
    const TP2: number[] = new Array(len).fill(0);
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");
    let prevSignal: Direction = DIR_NONE;
    let lastSignalBar = -1;
    const lastClosedBar = len - 2;

    for (let i = 1; i <= lastClosedBar; i++) {
      const bb = bbSignal[i];
      const tm = tmSignal[i];

      if (bb === tm && bb !== DIR_NONE) {
        const isNewSignal = bb !== prevSignal;
        if (isNewSignal) {
          SIGNAL[i] = bb;
          ENTRY[i] = roundToTick(candles[i].close, tick);
          SL[i] = roundToTick(bbStop[i], tick);
          const risk = Math.abs(ENTRY[i] - SL[i]);
          if (bb === DIR_LONG) {
            TP1[i] = roundToTick(ENTRY[i] + TP1_MULT * risk, tick);
            TP2[i] = roundToTick(ENTRY[i] + TP2_MULT * risk, tick);
          } else {
            TP1[i] = roundToTick(ENTRY[i] - TP1_MULT * risk, tick);
            TP2[i] = roundToTick(ENTRY[i] - TP2_MULT * risk, tick);
          }
          prevSignal = bb;
          lastSignalBar = i;
          if (bb === DIR_LONG) {
            markers.push({ time: candles[i].time, position: "belowBar", color: "#00B450", shape: "arrowUp", text: "", size: 2 });
            arrowMarkers.push({ time: candles[i].time, direction: "long", price: candles[i].low, type: "signal", position: "below" });
          } else {
            markers.push({ time: candles[i].time, position: "aboveBar", color: "#DC3232", shape: "arrowDown", text: "", size: 2 });
            arrowMarkers.push({ time: candles[i].time, direction: "short", price: candles[i].high, type: "signal", position: "above" });
          }
        } else {
          SIGNAL[i] = SIGNAL[i - 1] || prevSignal;
          ENTRY[i] = ENTRY[i - 1]; SL[i] = SL[i - 1];
          TP1[i] = TP1[i - 1]; TP2[i] = TP2[i - 1];
        }
      } else {
        SIGNAL[i] = prevSignal;
        if (i > 0) {
          ENTRY[i] = ENTRY[i - 1]; SL[i] = SL[i - 1];
          TP1[i] = TP1[i - 1]; TP2[i] = TP2[i - 1];
        }
      }

      // Candle color
      if (SIGNAL[i] === DIR_LONG) candleColorState[i] = "long";
      else if (SIGNAL[i] === DIR_SHORT) candleColorState[i] = "short";
      else candleColorState[i] = "neutral";
      // Neutral override when BB and TM disagree
      if (bb !== tm || bb === DIR_NONE) candleColorState[i] = "neutral";
    }

    // Forward-fill to forming bar
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      for (let i = lastClosedBar + 1; i < len; i++) {
        SIGNAL[i] = SIGNAL[lastClosedBar]; ENTRY[i] = ENTRY[lastClosedBar];
        SL[i] = SL[lastClosedBar]; TP1[i] = TP1[lastClosedBar]; TP2[i] = TP2[lastClosedBar];
        candleColorState[i] = candleColorState[lastClosedBar];
      }
    }

    // ═══════════════════════════════════════════════════════════════
    // 4. Hit detection (AFL approach)
    // ═══════════════════════════════════════════════════════════════
    let tp1Hit = false, tp2Hit = false, slHit = false;
    let tp1HitBar = -1, tp2HitBar = -1, slHitBar = -1;
    let bookedPL = 0;
    let status: AlphaTradeV2Dashboard["status"] = "Active";

    if (lastSignalBar >= 0 && ENTRY[lastSignalBar] > 0) {
      const sigDir = SIGNAL[lastSignalBar];
      const entryVal = ENTRY[lastSignalBar];
      const tp1Val = TP1[lastSignalBar];
      const tp2Val = TP2[lastSignalBar];
      const slVal = SL[lastSignalBar];
      for (let i = lastSignalBar + 1; i < len; i++) {
        if (sigDir === DIR_LONG) {
          if (!tp2Hit && candles[i].high >= tp2Val) { tp2Hit = true; tp2HitBar = i; if (!tp1Hit) { tp1Hit = true; tp1HitBar = i; } }
          else if (!tp1Hit && candles[i].high >= tp1Val) { tp1Hit = true; tp1HitBar = i; }
          if (!slHit && candles[i].low <= slVal) { slHit = true; slHitBar = i; }
        } else if (sigDir === DIR_SHORT) {
          if (!tp2Hit && candles[i].low <= tp2Val) { tp2Hit = true; tp2HitBar = i; if (!tp1Hit) { tp1Hit = true; tp1HitBar = i; } }
          else if (!tp1Hit && candles[i].low <= tp1Val) { tp1Hit = true; tp1HitBar = i; }
          if (!slHit && candles[i].high >= slVal) { slHit = true; slHitBar = i; }
        }
      }
      // Priority: TP2 > TP1 > SL > Active (matching AFL)
      if (tp2Hit) { status = "Target2 Hit"; bookedPL = sigDir === DIR_LONG ? tp2Val - entryVal : entryVal - tp2Val; }
      else if (tp1Hit) { status = "Target1 Hit"; bookedPL = sigDir === DIR_LONG ? tp1Val - entryVal : entryVal - tp1Val; }
      else if (slHit) { status = "Stop Loss Hit"; bookedPL = sigDir === DIR_LONG ? slVal - entryVal : entryVal - slVal; }

      // ─── Hit markers: icon + badge at first hit candle ───
      const isLong = sigDir === DIR_LONG;
      if (tp1HitBar >= 0 && tp1HitBar !== tp2HitBar) {
        markers.push({ time: candles[tp1HitBar].time, position: isLong ? "aboveBar" : "belowBar", color: "#00E676", shape: "circle", text: "", size: 1 });
        arrowMarkers.push({ time: candles[tp1HitBar].time, direction: isLong ? "long" : "short", price: isLong ? candles[tp1HitBar].high : candles[tp1HitBar].low, type: "tp1", position: isLong ? "above" : "below" });
      }
      if (tp2HitBar >= 0) {
        markers.push({ time: candles[tp2HitBar].time, position: isLong ? "aboveBar" : "belowBar", color: "#00E676", shape: "circle", text: "", size: 1 });
        arrowMarkers.push({ time: candles[tp2HitBar].time, direction: isLong ? "long" : "short", price: isLong ? candles[tp2HitBar].high : candles[tp2HitBar].low, type: "tp2", position: isLong ? "above" : "below" });
      }
      if (slHitBar >= 0) {
        markers.push({ time: candles[slHitBar].time, position: isLong ? "belowBar" : "aboveBar", color: "#FF6D00", shape: "circle", text: "", size: 1 });
        arrowMarkers.push({ time: candles[slHitBar].time, direction: isLong ? "long" : "short", price: isLong ? candles[slHitBar].low : candles[slHitBar].high, type: "sl", position: isLong ? "below" : "above" });
      }
    }

    // ═══════════════════════════════════════════════════════════════
    // 5. Dashboard metadata
    // ═══════════════════════════════════════════════════════════════
    const uiBar = Math.max(0, len - 2);
    const currentPrice = candles[len - 1]?.close ?? 0;
    const currSignal = SIGNAL[uiBar];
    const currEntry = ENTRY[uiBar];
    const currBB = bbSignal[uiBar];
    const currTM = tmSignal[uiBar];
    let dashSignal: AlphaTradeV2Dashboard["signal"] = null;
    let dashTrend: AlphaTradeV2Dashboard["trend"] = "NEUTRAL";
    let livePL = 0;
    if (currSignal === DIR_LONG && currEntry > 0) {
      dashSignal = "BUY"; dashTrend = "UP"; livePL = currentPrice - currEntry;
    } else if (currSignal === DIR_SHORT && currEntry > 0) {
      dashSignal = "SELL"; dashTrend = "DOWN"; livePL = currEntry - currentPrice;
    }
    if (currBB !== currTM || currBB === DIR_NONE) dashTrend = "NEUTRAL";

    const dashboard: AlphaTradeV2Dashboard = {
      signal: dashSignal, trend: dashTrend,
      entry: currEntry > 0 ? currEntry : null,
      stopLoss: SL[uiBar] > 0 ? SL[uiBar] : null,
      target1: TP1[uiBar] > 0 ? TP1[uiBar] : null,
      target2: TP2[uiBar] > 0 ? TP2[uiBar] : null,
      currentPrice, livePL, bookedPL, status,
      tp1Hit, tp2Hit, slHit,
      candleState: candleColorState[uiBar] || "neutral",
    };

    // ═══════════════════════════════════════════════════════════════
    // 6. Plot data
    // ═══════════════════════════════════════════════════════════════
    const entryData: { time: number; value: number }[] = [];
    const slData: { time: number; value: number }[] = [];
    const t1Data: { time: number; value: number }[] = [];
    const t2Data: { time: number; value: number }[] = [];

    // Plot series intentionally left empty — priceLines handle all level drawing.

    // ═══════════════════════════════════════════════════════════════
    // 7. Price lines with fromTime (extend to right edge)
    // ═══════════════════════════════════════════════════════════════
    const priceLines: IndicatorPriceLine[] = [];
    if (displayLevels !== "no" && lastSignalBar >= 0 && ENTRY[lastSignalBar] > 0) {
      const ft = candles[lastSignalBar].time;
      priceLines.push(
        { id: "atv-entry", price: ENTRY[lastSignalBar], color: "#FFFFFF", title: "Entry",    lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: ft },
        { id: "atv-sl",    price: SL[lastSignalBar],    color: "#FFFF00", title: "Stop Loss", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: ft },
        { id: "atv-tp1",   price: TP1[lastSignalBar],   color: "#00FF00", title: "TGT-1",    lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: ft },
        { id: "atv-tp2",   price: TP2[lastSignalBar],   color: "#00FF00", title: "TGT-2",    lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: ft },
      );
    }

    // Candle colors
    const candleColors: { time: number; color: string }[] = [];
    if (showBackground !== "no") for (let i = 0; i < len; i++) {
      const s = candleColorState[i];
      candleColors.push({ time: candles[i].time, color: s === "long" ? "#00B450" : s === "short" ? "#DC3232" : "#787878" });
    }

    return [
      { key: "entry", data: entryData, markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "stopLoss", data: slData },
      { key: "target1", data: t1Data },
      { key: "target2", data: t2Data },
    ];
  },
};
